import {
    ChangeEvent, DataSource, DropdownItem, Map, MapPin, MapVendor, Panel, Snackbar, Switch, Textbox
} from "@mcleod/components";
import { ModelRow } from "@mcleod/core";
import { OrderStatus } from "./CustomerPortal";
import { TableCustomerOrders } from "./TableCustomerOrders";

interface dayCaption {
    orderStatus: OrderStatus,
    origin: string,
    destination: string;
}

export class TableCustomerOrdersMap extends Panel {
    public maxPins = 1000;
    private dayCaptions: dayCaption[] = [
        { orderStatus: OrderStatus.AVAILABLE, origin: "Scheduled pickup window is", destination: "Scheduled delivery window is" },
        { orderStatus: OrderStatus.PROGRESS, origin: "Actual pickup was", destination: "Scheduled delivery window is" },
        { orderStatus: OrderStatus.DELIVERED, origin: "Actual pickup was", destination: "Actual delivery was" }
    ];

    private tableCustomerOrders: TableCustomerOrders;
    private panelOriginDest = new Panel({ height: 70, fillRow: true });
    private switchOriginDest = new Switch({
        id: "switchOriginDest", width: 203, marginTop: 15, rowBreak: false,
        captionVisible: false, leftCaption: "Origin", rightCaption: "Destination", checked: true
    });
    private textboxDays = new Textbox({ id: "textboxDays", width: 300, allowDropdownBlank: false, }); x
    mapOrders: Map;

    private get orderStatus(): OrderStatus {
        return this.tableCustomerOrders.orderStatus;
    }

    private get mainDataSource(): DataSource {
        return this.tableCustomerOrders.mainDataSource;
    }

    constructor(layout: TableCustomerOrders) {
        super({ fillHeight: true, fillRow: true, padding: 0 });
        this.tableCustomerOrders = layout;
        this.addOriginDestPanel();
        this.addMap();
    }

    addOriginDestPanel() {
        this.switchOriginDest.checked = false;
        this.switchOriginDest.addChangeListener((event: ChangeEvent) => this.switchOriginDestOnChange(event));
        this.panelOriginDest.add(this.switchOriginDest);
        if (this.orderStatus != OrderStatus.ALL) {
            this.setDaysCaption();
            this.setTextboxDayItems();
            this.panelOriginDest.add(this.textboxDays);
            this.textboxDays.addChangeListener((event: ChangeEvent) => this.textboxDaysOnChange(event));
        }
        this.add(this.panelOriginDest);
    }

    addMap() {
        this.mapOrders = new Map({
            borderRadius: 4, fillHeight: true, fillRow: true, id: "map1",
            latitudeField: "shipper.latitude", longitudeField: "shipper.longitude",
            pinLayout: "portal-customer/PopupTableCustomerOrders",
            vendor: MapVendor.DEFAULT_DISTANCE_VENDOR
        })
        this.mapOrders.setPopupProps({
            top: window.innerHeight * .25,
            left: window.innerWidth * .025,
            width: window.innerWidth * .95,
            height: window.innerHeight * .65
        });
        this.add(this.mapOrders);
    }

    public plotPins(pinData: ModelRow[]) {
        let summaryPins = this.summarizePinData(pinData);
        if (summaryPins?.length > this.maxPins) {
            Snackbar.showWarningSnackbar("Only the first " + this.maxPins + " orders will be plotted on this map.");
            summaryPins = summaryPins.slice(0, this.maxPins);
        }
        this.mapOrders.removeAllPins();
        for (const pin of summaryPins) {    
            this.mapOrders.addPin(pin);
        }
        this.mapOrders.fitPins();
    }

    private summarizePinData(allPinData: ModelRow[]): MapPin[] {
        const pinDataByLocation = {};
        for (const pinData of allPinData) {
            const latitude = pinData.get(this.mapOrders.latitudeField);
            const longitude = Math.abs(pinData.get(this.mapOrders.longitudeField)) * -1;
            const key = latitude + "," + longitude;
            let pin = pinDataByLocation[key];
            if (pin == null) {
                pin = new MapPin(latitude, longitude);
                pin.data = pinData;
                pin.tooltip = pinData.get("orders.id");
                pin.image = "map-pin-white-dot-small";
                pinDataByLocation[key] = pin;
            } else if (pin.rows.length === 1) {
                pin.tooltip = pinData.get("shipper_name");
                pin.image = "map-pin-white-dot-large";
                pin.captionColor = "Blue";
            }
            pin.rows.push(pinData);
        }
        const result = Object.values(pinDataByLocation) as MapPin[];
        for (const pin of result) {
            if (pin.rows.length > 1) {
                pin.caption = pin.rows.length;
                pin.tooltip = pin.rows.length + " orders at this exact location.";
            }
        }
        return Object.values(pinDataByLocation);
    }

    textboxDaysOnChange(event: ChangeEvent) {
        if (event.userInitiatedChange)
            this.searchMap();
    }

    private searchMap() {
        this.mainDataSource.search({
            status: this.orderStatus,
            date_offset: this.textboxDays?.selectedItem?.value ?? 0,
            is_origin: this.switchOriginDest.checked ? "N" : "Y"
        });
    }

    switchOriginDestOnChange(event: ChangeEvent) {
        if (event.userInitiatedChange) {
            if (this.orderStatus === OrderStatus.PROGRESS)
                this.setTextboxDayItems();
            this.setDaysCaption();

            this.mapOrders.latitudeField = event.newValue ? "consignee.latitude" : "shipper.latitude";
            this.mapOrders.longitudeField = event.newValue ? "consignee.longitude" : "shipper.longitude";
            this.searchMap();
        }
    }

    public setDaysCaption() {
        const dayCaption = this.dayCaptions.find(dayCaptions => this.orderStatus == dayCaptions.orderStatus)
        this.textboxDays.caption = this.switchOriginDest.checked ? dayCaption?.destination : dayCaption?.origin;
    }

    private setTextboxDayItems() {
        let lastItem: DropdownItem = null;
        let futureDate = false;

        if (this.orderStatus === OrderStatus.AVAILABLE) {
            futureDate = true;
            lastItem = new DropdownItem("0", "Any day (all available loads)");
        }
        if (this.orderStatus === OrderStatus.PROGRESS) {
            futureDate = this.switchOriginDest.checked
            lastItem = new DropdownItem("0", "Any day (all in progress loads)");
        }
        const dayPrefix = futureDate ? "next" : "last";
        const items: DropdownItem[] = [
            new DropdownItem("1", futureDate ? "Today or tomorrow" : "Yesterday or today" ),
            new DropdownItem("3", `Within the ${dayPrefix} 3 days`),
            new DropdownItem("5", `Within the ${dayPrefix} 5 days`),
            new DropdownItem("7", `Within the ${dayPrefix} 7 days`),
            new DropdownItem("14", `Within the ${dayPrefix} 14 days`)
        ];
        if (lastItem)
            items.push(lastItem);

        this.textboxDays.items = items;
        this.textboxDays.selectedItem = items[items.length - 1];
    }
}
